import React from 'react';
import { Archive, Eye, RefreshCw, Search, Trash2, Users } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';

export const ImpersonateTab = ({ deletingTactical, diagnosticEmail, diagnosticResult, handleArchiveTactical, handleDeleteTactical, handleImpersonate, handleRestoreTactical, impersonating, loadUsers, loadingDiagnostic, loadingUsers, restoringTactical, runDiagnostic, setDiagnosticEmail, setUserSearch, userSearch, usersList }) => (
            <TabsContent value="impersonate">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Impersonate User
                  </CardTitle>
                  <CardDescription>
                    Log in as any user to troubleshoot issues. You'll see exactly what they see. Use "Return to Admin" to come back.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Search Bar */}
                  <div className="flex gap-3 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by name or email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadUsers(userSearch)}
                        className="pl-10"
                        data-testid="impersonate-search-input"
                      />
                    </div>
                    <Button onClick={() => loadUsers(userSearch)} variant="outline" data-testid="impersonate-search-btn">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    <Button onClick={() => { setUserSearch(''); loadUsers(''); }} variant="ghost">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      All
                    </Button>
                  </div>
  
                  {loadingUsers ? (
                    <div className="space-y-3">
                      {[1,2,3].map(i => (
                        <div key={`skeleton-${i}`} className="h-16 bg-slate-100 rounded animate-pulse" />
                      ))}
                    </div>
                  ) : usersList.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>No users found. Click "All" to load all users.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground mb-3">
                        {usersList.length} user(s) found
                      </p>
                      {usersList.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                          data-testid={`impersonate-user-row-${u.id}`}
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{u.display_name}</p>
                              <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                            <div className="text-right hidden md:block">
                              {u.has_current_tactical ? (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                  Tactical: {u.current_tactical_month}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                  No tactical
                                </Badge>
                              )}
                              {u.manager_email && (
                                <p className="text-xs text-muted-foreground mt-1">Mgr: {u.manager_email}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleImpersonate(u.id, u.display_name)}
                              disabled={impersonating === u.id}
                              data-testid={`impersonate-btn-${u.id}`}
                            >
                              {impersonating === u.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View As
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
  
              {/* Tactical Diagnostics */}
              <Card className="border-slate-200 mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Tactical Diagnostics
                  </CardTitle>
                  <CardDescription>
                    Search for ALL tacticals by email — finds data across ghost accounts (multiple user IDs for the same person). Use this to find lost data or recover completed tacticals.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3 mb-6">
                    <Input
                      placeholder="Enter user email (e.g. aspeer@therapymgmt.com)"
                      value={diagnosticEmail}
                      onChange={(e) => setDiagnosticEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && runDiagnostic()}
                      className="flex-1"
                      data-testid="diagnostic-email-input"
                    />
                    <Button onClick={() => runDiagnostic()} disabled={loadingDiagnostic} data-testid="diagnostic-search-btn">
                      {loadingDiagnostic ? 'Searching...' : 'Diagnose'}
                    </Button>
                  </div>
  
                  {diagnosticResult && (
                    <div className="space-y-4">
                      {/* User Records */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold mb-2">User Accounts Found: {diagnosticResult.user_records.length}</h4>
                        {diagnosticResult.user_records.map((u) => (
                          <div key={u.id || u.email} className="text-xs text-muted-foreground">
                            <span className="font-mono">{u.id}</span> — {u.display_name} ({u.email}) {diagnosticResult.user_records.length > 1 && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 ml-1">Ghost Account</Badge>}
                          </div>
                        ))}
                      </div>
  
                      {/* Tacticals */}
                      <h4 className="text-sm font-semibold">Tacticals Found: {diagnosticResult.total_tacticals}</h4>
                      {diagnosticResult.tacticals.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No tacticals found for this email.</p>
                      ) : (
                        <div className="space-y-2">
                          {diagnosticResult.tacticals.map((t) => {
                            const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                            const monthName = monthNames[t.month - 1] || '?';
                            const hasData = t.has_development_data || t.has_lightning_round || t.scorecard_filled !== '0/0';
                            const sitesEmpty = t.facility_sites.every(s => s.items_with_data === 0);
                            
                            return (
                              <div key={t.id} className={`p-4 rounded-lg border ${t.is_archived ? 'bg-slate-50 border-slate-200' : 'bg-white border-primary/30'}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div>
                                      <span className="font-semibold text-sm">{monthName} {t.year}</span>
                                      {t.is_archived ? (
                                        <Badge variant="outline" className="ml-2 text-xs">Archived</Badge>
                                      ) : (
                                        <Badge className="ml-2 text-xs bg-green-100 text-green-800 border-green-200">Active</Badge>
                                      )}
                                      {t.has_tah && <Badge variant="outline" className="ml-1 text-xs">TAH</Badge>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* Archive: for active tacticals that aren't the most recent */}
                                    {!t.is_archived && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleArchiveTactical(t.id, t.year, t.month)}
                                        disabled={restoringTactical === t.id}
                                        data-testid={`archive-btn-${t.id}`}
                                      >
                                        <Archive className="w-3 h-3 mr-1" />
                                        {restoringTactical === t.id ? '...' : 'Archive'}
                                      </Button>
                                    )}
                                    {/* Restore: for archived tacticals */}
                                    {t.is_archived && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleRestoreTactical(t.id)}
                                        disabled={restoringTactical === t.id}
                                        data-testid={`restore-btn-${t.id}`}
                                      >
                                        {restoringTactical === t.id ? '...' : 'Restore'}
                                      </Button>
                                    )}
                                    {/* Delete: for any tactical */}
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteTactical(t.id, t.year, t.month)}
                                      disabled={deletingTactical === t.id}
                                      data-testid={`delete-btn-${t.id}`}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      {deletingTactical === t.id ? '...' : 'Delete'}
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                  <div className={`px-2 py-1 rounded ${t.has_development_data ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                    Dev: {t.has_development_data ? 'Has Data' : 'Empty'}
                                  </div>
                                  <div className={`px-2 py-1 rounded ${t.scorecard_filled !== '0/0' && !t.scorecard_filled.startsWith('0/') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                    Scorecard: {t.scorecard_filled}
                                  </div>
                                  <div className={`px-2 py-1 rounded ${t.has_lightning_round ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                    Lightning: {t.has_lightning_round ? 'Has Data' : 'Empty'}
                                  </div>
                                  <div className={`px-2 py-1 rounded ${!sitesEmpty ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                    Sites: {t.facility_sites.map(s => `${s.name}(${s.items_with_data}/${s.total_items})`).join(', ') || 'None'}
                                  </div>
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                  user_id: <span className="font-mono">{t.user_id}</span> | id: <span className="font-mono">{t.id.substring(0, 8)}...</span> | updated: {t.updated_at ? new Date(t.updated_at).toLocaleString() : 'N/A'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
);
